import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import ButtonAction from "../../../../../components/buttons/ButtonAction";
import { AddIcon, AppleIcon, CheckCardIcon } from "../../../../../components/buttons/IconButtons";
import { CustomCardGrid } from "../../../../../components/CustomCardGrid";
import CustomChip from "../../../../../components/CustomChip";
import CustomTooltip from "../../../../../components/CustomTooltip";
import { colors } from "../../../../../enums/colors";
import { permissionObj } from "../../../../../Helpers/permission";
import renderAllergenIcons from "../../../Dining/Dishes/SubComponents/AllergenIcons";
import { MealTypeEnum } from "../../../Dining/Menu/Model/Model";
import UnselectedCard from "../../SubComponents/UnselectedCard";
import DishCardModal from "./DishCardModal";
import {useLocation} from "react-router-dom";
import {currencyFormat} from '../../../../../Apis/functions';



/**
 * @Author      Jhovanny Fragoso Marquez
 * @JobTitle    Frontend Developer
 * @Email       jfragoso@ibl.mx
 *
 * Renders an interactive card representing a dish.
 * @component
 * @param {Object} props - Component props.
 * @param {Object} props.dish - Dish object containing dish details and metadata.
 * @param {Function} props.onClick - Callback triggered when the dish is selected or deselected.
 * @param {string} [props.className] - Optional additional class names for styling.
 * @param {boolean} [props.disableButtons=false] - If true, disables the action buttons.
 * @param {boolean} [props.isSelected=false] - Indicates if the dish is currently selected. Affects overlay rendering.
 *
 * @example
 * <DishCard dish={dishData} onClick={handleDishClick} />
 */


const DishCard = ({ dish, onClick, className = '', disableButtons = false, isSelected }) => {
    const { t } = useTranslation();
    const location = useLocation();
    const [showModal, setShowModal] = useState(false);
    const [isHovered, setIsHovered] = useState(false)
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const { permission } = useSelector((state) => state.authenticatioauthennSlice);
    const { getFirstFoodieSetting: foodieSetting } = useSelector((state) => state.employeeFoodieSlice);
    const [contractorId, setContractorId] = useState(sessionStorage.getItem('contractorId'));
    const [supplierId, setSupplierId] = useState(sessionStorage.getItem('providerId'));
    let { state } = location; // Get state from location
    let servingDate = state?.servingDate; // Get state from location
    const foodieItem = dish.foodieItem || {};

    //Toggles the dish selection state and triggers the `onClick` callback.
    const toggleDishSelection = () => {
        onClick?.(dish.id);
    };

    const handleOpen = () => setShowModal(true);
    const handleClose = () => setShowModal(false);


    return (
        <div
            className={`position-relative ${className}`}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {isSelected && isHovered && (
                <div className="hover-delete-overlay show" onClick={toggleDishSelection}>
                    <UnselectedCard />
                </div>
            )}

            {isSelected && !isHovered && (
                <div className="checked-card-overlay">
                    <CheckCardIcon className='w-130px-h-130px icon-color-secondary-500' onClick={toggleDishSelection} />
                </div>
            )}

            {isSelected && (
                <div className="selected-overlay" />
            )}

            <CustomCardGrid
                key={dish.id}
                isCheckboxShow={false}
                itemId={dish.id}
                checked={false}
                onCheckboxChange={() => onClick(dish.id)}
                disabled={false}
                statusColor={colors.secondary100}
                fontColor={colors.secondary700}
                borderColor={colors.secondary150}
                showCustomChip={false}
                srcImage={foodieItem.image ? `data:image/png;base64,${foodieItem.image}` : <AppleIcon />}
                isSquareImg={true}
                classNamePanelGrid={""} // className empty to remove cols classes in CustomCardGrid
                height={`meal-item-card`}
                isDefaultIcon={!foodieItem.image}
                btnActions={
                    <div className="d-flex justify-content-end gap-2">
                        <div>
                            <ButtonAction
                                id="btn_dc_view_details"
                                name={t('view_details')}
                                splitName={false}
                                className="btn-outlined-secondary-600-00"
                                disabled={disableButtons}
                                Icon={AddIcon}
                                onClick={() => handleOpen()}
                            />
                        </div>
                        {(DEV || supplierId || contractorId || servingDate || permission.includes(permissionObj?.WEB_FOODIE_MENU_SELECTION_CREATE)) && (
                            <div>
                                <ButtonAction
                                    id="btn_dc_select"
                                    name={t('select')}
                                    className="btn-icon-export"
                                    disabled={disableButtons}
                                    Icon={AddIcon}
                                    onClick={toggleDishSelection}
                                />
                            </div>
                        )}
                    </div>
                }
            >
                <div className="d-flex gap-2 position-absolute r-16px t-16px">
                    <CustomChip
                        background={colors.secondary100}
                        borderColor={colors.secondary150}
                        fontColor={colors.secondary700}
                        legend={t(MealTypeEnum[dish.foodieMealTypeId]?.name)}
                    />
                    <CustomChip
                        background={colors.success100}
                        borderColor={colors.success150}
                        fontColor={colors.success700}
                        legend={t(dish.active ? 'active' : 'inactive')}
                    />
                </div>

                <div className="d-flex justify-content-between">
                    <div className="fs-12px ibl-font-w-600 font-color-general-950 pt-2">{t('name')}</div>
                    <div className="fs-14px p-2">{foodieItem.name}</div>
                </div>

                <div className="pb-2">
                    <p className="fs-12px ibl-font-w-600 font-color-general-950 pt-2 mb-1">{t('description')}</p>
                    <CustomTooltip
                        text={foodieItem.description}
                        showIfTruncated={true}
                        classes="text-ellipsis-2-rows fs-14px"
                    />
                </div>

                {!foodieSetting?.isBaseCost &&
                    <div className="pb-2">
                        <div className="fs-12px ibl-font-w-600 font-color-general-950 pt-2">{t('cost')}</div>
                        <div className="fs-14px">{"$ " + currencyFormat(dish.cost)}</div>
                    </div>
                }

                {renderAllergenIcons(dish.foodieItem, t)}
            </CustomCardGrid>

            <DishCardModal
                dish={dish}
                handleClose={handleClose}
                showModal={showModal}
            />
        </div>
    );
};

export default DishCard;
